import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Box,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Button,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    ContactsOutlinedIcon,
    ExtensionOutlinedIcon,
    EmailOutlinedIcon,
    SettingsOutlinedIcon,
    MoreVertIcon,
    LogoutOutlinedIcon,
    FeedbackOutlinedIcon,
    RocketLaunchOutlinedIcon,
    InfoIcon,
    KeyboardArrowDownIcon
} from 'icons/materialIcons';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import PowerOutlinedIcon from '@mui/icons-material/PowerOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import { useAuth } from 'contexts/AuthContext';
import avatarPlaceholder from 'images/avatar-cropped.png';
import { getUserById } from 'apis/authApi';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import { getDefaultCardByUserId } from 'apis/cardApi';
import TeamRequestAFeatureModal from './support/TeamRequestAFeatureModal';

const TeamSidenav = ({ selectedMenu, onMenuClick }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const navigate = useNavigate();
    const [userData, setUserData] = useState(null);
    const { logout, authState } = useAuth();
    const [profilePicture, setProfilePicture] = useState(avatarPlaceholder);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [isExpanded, setIsExpanded] = useState(!isSmallScreen);
    const [openSubmenus, setOpenSubmenus] = useState({});
    const userName = authState?.user?.fullName || 'User';
    const userId = authState?.user?.userId;

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);

    // Effect to handle screen size changes
    useEffect(() => {
        setIsExpanded(!isSmallScreen);
    }, [isSmallScreen]);

    const handleSidebarExpand = () => {
        if (isSmallScreen && !isExpanded) {
            setIsExpanded(true);
        }
    };

    const handleSidebarCollapse = () => {
        if (isSmallScreen && isExpanded) {
            setIsExpanded(false);
        }
    };

    const handleMenuItemClick = (menu) => {
        if (isSmallScreen && !isExpanded) {
            setIsExpanded(true);
        }
        onMenuClick(menu);
    };

    const handleSubmenuToggle = (menu) => {
        setOpenSubmenus(prev => {
            // Close all other submenus and toggle the current one
            const newState = {};
            Object.keys(prev).forEach(key => {
                newState[key] = false;
            });
            newState[menu] = !prev[menu];
            return newState;
        });
    };

    // Reusable menu styles
    const menuStyles = {
        // Main menu item styles
        mainItem: {
            justifyContent: (!isSmallScreen || isExpanded) ? 'flex-start' : 'center',
            px: (!isSmallScreen || isExpanded) ? '1.25rem' : '0.625rem',
            py: 1,
            cursor: 'pointer',
            position: 'relative',
            '&:hover': {
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '0.75rem',
                    right: '0.75rem',
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: '0.25rem',
                    zIndex: -1
                }
            }
        },
        mainIcon: {
            fontSize: '1.25rem',
            color: '#828282'
        },
        mainText: {
            fontSize: '0.8rem',
            fontWeight: 600,
            color: '#4f4f4f',
            marginLeft: 0
        },
        dropdownIcon: {
            fontSize: '1rem',
            color: '#828282',
            ml: 1,
            transition: 'transform 0.3s ease-in-out'
        },
        // Submenu styles
        submenuContainer: {
            pl: '1.25rem',
            overflow: 'hidden'
        },
        verticalLine: {
            position: 'absolute',
            left: '1rem',
            top: 0,
            bottom: 0,
            width: '0.0625rem',
            backgroundColor: '#e0e0e0',
            boxShadow: '0.0625rem 0 0.125rem rgba(0, 0, 0, 0.1)'
        },
        submenuItem: {
            px: '1.25rem',
            py: 1,
            pl: '2.5rem',
            cursor: 'pointer',
            position: 'relative',
            '&:hover': {
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '1.375rem',
                    right: '0.75rem',
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: '0.25rem',
                    zIndex: -1
                }
            }
        },
        submenuText: {
            fontSize: '0.8rem',
            fontWeight: 600,
            color: '#4f4f4f'
        }
    };

    const submenuItems = {
        team: [
            { text: 'Members', onClick: () => handleMenuItemClick('team-members') },
            { text: 'Subteams', onClick: () => handleMenuItemClick('team-subteams') },
            { text: 'Templates', onClick: () => handleMenuItemClick('team-templates') },
            { text: 'Add Members', onClick: () => handleMenuItemClick('team-add-members') }
        ],
        leads: [
            { text: 'Leads', onClick: () => handleMenuItemClick('leads') },
            { text: 'Import Leads', onClick: () => handleMenuItemClick('leads-import') }
        ],
        admin: [
            { text: 'Team Assets', onClick: () => handleMenuItemClick('admin-assets') },
            { text: 'API Keys', onClick: () => handleMenuItemClick('admin-api-keys') },
            { text: 'Invite Emails', onClick: () => handleMenuItemClick('admin-invite-emails') },
            { text: 'Notifications', onClick: () => handleMenuItemClick('admin-notifications') }
        ],
        settings: [
            { text: 'Team Settings', onClick: () => handleMenuItemClick('settings-team') },
            { text: 'My Subscription', onClick: () => handleMenuItemClick('settings-subscription') }
        ]
    };

    // Reusable submenu component
    const SubmenuComponent = ({ submenuKey, items, isOpen }) => (
        <Box
            sx={{
                ...menuStyles.submenuContainer,
                maxHeight: isOpen ? 'none' : '0rem',
                transition: isOpen ? 'max-height 0.5s ease-out, opacity 0.5s ease-out' : 'max-height 0.3s ease-in, opacity 0.3s ease-in',
                opacity: isOpen ? 1 : 0,
                mb: isOpen ? 1 : 0
            }}
        >
            <Box sx={{ position: 'relative' }}>
                <Box sx={menuStyles.verticalLine} />
                {items.map((subItem) => (
                    <ListItem
                        key={subItem.text}
                        onClick={subItem.onClick}
                        sx={menuStyles.submenuItem}
                    >
                        <ListItemText
                            primary={subItem.text}
                            primaryTypographyProps={{
                                sx: menuStyles.submenuText
                            }}
                        />
                    </ListItem>
                ))}
            </Box>
        </Box>
    );

    const handleRequestFeature = () => {
        setIsFeatureModalOpen(true);
    };

    const handleFeatureSubmitSuccess = () => {
        setSnackbar({
            open: true,
            message: 'Feature request submitted! Our team will review it.',
            severity: 'success'
        });
    };

    const handleFeatureSubmitError = (error) => {
        setSnackbar({
            open: true,
            message: error || 'Failed to submit feature request. Please try again later.',
            severity: 'error'
        });
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleAppClick = () => {
    };

    const handleMenuClick = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleSubscribe = () => {
        handleClose();
        navigate(`/dashboard/subscribe`);
    };

    useEffect(() => {
        fetchUserData();
    }, [authState?.user?.userId]);

    const fetchUserData = async () => {
        try {
            const idToken = localStorage.getItem('authToken');
            if (!idToken) {
                throw new Error('No auth token found');
            }
            const response = await getUserById(userId, idToken);
            setUserData(response.data);
        } catch (error) {
            console.error('Error fetching user data:', error.message);
        }
    };

    useEffect(() => {
        const fetchDefaultCardProfile = async () => {
            try {
                if (userId) {
                    let lastError = null;
                    const maxRetries = 3;
                    const baseDelay = 1000;

                    for (let attempt = 0; attempt <= maxRetries; attempt++) {
                        try {
                            console.log(`🔍 TeamSidenav: Attempt ${attempt + 1}/${maxRetries + 1} to fetch default card profile`);

                            const defaultCard = await getDefaultCardByUserId(userId);
                            if (defaultCard && defaultCard.profilePictureUrl) {
                                setProfilePicture(defaultCard.profilePictureUrl);
                                console.log('🔍 TeamSidenav: Default card profile picture set successfully');
                                return;
                            } else {
                                console.log('🔍 TeamSidenav: Default card found but no profile picture URL');
                                return;
                            }
                        } catch (error) {
                            lastError = error;
                            console.log(`🔍 TeamSidenav: Attempt ${attempt + 1} failed:`, error.message);

                            if (attempt === maxRetries) {
                                break;
                            }

                            const delay = baseDelay * Math.pow(2, attempt);
                            console.log(`🔍 TeamSidenav: Waiting ${delay}ms before retry...`);
                            await new Promise(resolve => setTimeout(resolve, delay));
                        }
                    }

                    console.error('🔍 TeamSidenav: All retry attempts failed for fetching default card profile:', lastError);
                }
            } catch (error) {
                console.error('Error fetching default card profile:', error);
            }
        };

        fetchDefaultCardProfile();
    }, [userId]);

    return (
        <>
            <Drawer
                sx={{
                    width: isSmallScreen ? (isExpanded ? '13rem' : '3.5rem') : '13rem',
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: isSmallScreen ? (isExpanded ? '13rem' : '3.5rem') : '13rem',
                        boxSizing: 'border-box',
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                        overflowX: 'hidden',
                        overflowY: 'hidden',
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    },
                }}
                variant="permanent"
                anchor="left"
            >
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Logo Section */}
                    <Box sx={{ paddingTop: '1.875rem', paddingBottom: '0.625rem', display: 'flex', alignItems: 'center', marginLeft: '1.25rem', justifyContent: 'start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: '#333',
                                    fontSize: isExpanded ? '1.1rem' : '0.9rem',
                                    letterSpacing: '0.03125rem',
                                    textTransform: 'none',
                                    textAlign: 'center'
                                }}>
                                Touch
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#4f4f4f',
                                    fontSize: isExpanded ? '1.1rem' : '0.9rem',
                                    letterSpacing: '0.03125rem',
                                    textTransform: 'none',
                                    marginLeft: '0.25rem'
                                }}>
                                Teams
                            </Typography>
                        </Box>
                    </Box>

                    {/* Scrollable Navigation Menu */}
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                            width: '0.25rem',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#c1c1c1',
                            borderRadius: '0.125rem',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: '#a8a8a8',
                        },
                    }}>
                        <List sx={{ pt: 0 }}>
                            {[
                                { text: 'Home', icon: <HomeOutlinedIcon sx={menuStyles.mainIcon} />, onClick: () => handleMenuItemClick('home'), hasDropdown: false },
                                { text: 'Team', icon: <GroupOutlinedIcon sx={menuStyles.mainIcon} />, onClick: () => handleSubmenuToggle('team'), hasDropdown: true, submenuKey: 'team' },
                                { text: 'Leads', icon: <ContactsOutlinedIcon sx={menuStyles.mainIcon} />, onClick: () => handleSubmenuToggle('leads'), hasDropdown: true, submenuKey: 'leads' },
                                { text: 'Insights', icon: <BarChartOutlinedIcon sx={menuStyles.mainIcon} />, onClick: () => handleMenuItemClick('insights'), hasDropdown: false },
                                { text: 'Integrations', icon: <PowerOutlinedIcon sx={menuStyles.mainIcon} />, onClick: () => handleMenuItemClick('integrations'), hasDropdown: false },
                                { text: 'Email Signature', icon: <EmailOutlinedIcon sx={menuStyles.mainIcon} />, onClick: () => handleMenuItemClick('email-signature'), hasDropdown: false },
                                { text: 'Admin', icon: <BuildOutlinedIcon sx={{ ...menuStyles.mainIcon, transform: 'scaleX(-1)' }} />, onClick: () => handleSubmenuToggle('admin'), hasDropdown: true, submenuKey: 'admin' },
                                { text: 'Support', icon: <HelpOutlineOutlinedIcon sx={menuStyles.mainIcon} />, onClick: () => handleMenuItemClick('support'), hasDropdown: false },
                                { text: 'Settings', icon: <SettingsOutlinedIcon sx={menuStyles.mainIcon} />, onClick: () => handleSubmenuToggle('settings'), hasDropdown: true, submenuKey: 'settings' }
                            ].map((item) => (
                                <React.Fragment key={item.text}>
                                    <ListItem
                                        onClick={item.onClick}
                                        sx={menuStyles.mainItem}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: (!isSmallScreen || isExpanded) ? 40 : 'auto',
                                                justifyContent: 'center'
                                            }}
                                            onClick={(e) => {
                                                if (!isExpanded) {
                                                    e.stopPropagation();
                                                    handleSidebarExpand();
                                                }
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>
                                        {(!isSmallScreen || isExpanded) && (
                                            <>
                                                <ListItemText
                                                    primary={item.text}
                                                    primaryTypographyProps={{
                                                        sx: menuStyles.mainText
                                                    }}
                                                />
                                                {item.hasDropdown && (
                                                    <KeyboardArrowDownIcon
                                                        sx={{
                                                            ...menuStyles.dropdownIcon,
                                                            transform: openSubmenus[item.submenuKey] ? 'rotate(0deg)' : 'rotate(-90deg)'
                                                        }}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </ListItem>

                                    {/* Team Submenu */}
                                    {item.submenuKey === 'team' && (!isSmallScreen || isExpanded) && (
                                        <SubmenuComponent
                                            submenuKey="team"
                                            items={submenuItems.team}
                                            isOpen={openSubmenus.team}
                                        />
                                    )}

                                    {/* Leads Submenu */}
                                    {item.submenuKey === 'leads' && (!isSmallScreen || isExpanded) && (
                                        <SubmenuComponent
                                            submenuKey="leads"
                                            items={submenuItems.leads}
                                            isOpen={openSubmenus.leads}
                                        />
                                    )}

                                    {/* Admin Submenu */}
                                    {item.submenuKey === 'admin' && (!isSmallScreen || isExpanded) && (
                                        <SubmenuComponent
                                            submenuKey="admin"
                                            items={submenuItems.admin}
                                            isOpen={openSubmenus.admin}
                                        />
                                    )}

                                    {/* Settings Submenu */}
                                    {item.submenuKey === 'settings' && (!isSmallScreen || isExpanded) && (
                                        <SubmenuComponent
                                            submenuKey="settings"
                                            items={submenuItems.settings}
                                            isOpen={openSubmenus.settings}
                                        />
                                    )}
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>

                    {/* Fixed Bottom Section */}
                    <Box sx={{ flexShrink: 0 }}>
                        {/* User Avatar in collapsed view */}
                        {isSmallScreen && !isExpanded && (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mt: 2,
                                mb: 2
                            }}>
                                <Box
                                    sx={{
                                        cursor: 'pointer'
                                    }}
                                    onClick={handleSidebarExpand}
                                >
                                    <img
                                        src={profilePicture}
                                        alt="User Avatar"
                                        style={{
                                            width: '2.5rem',
                                            height: '2.5rem',
                                            borderRadius: '50%',
                                            objectFit: 'cover'
                                        }}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = avatarPlaceholder;
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}

                        {/* Expanded view content */}
                        {(!isSmallScreen || isExpanded) && (
                            <>
                                {/* Divider with shadow */}
                                <Box sx={{
                                    height: '0.0625rem',
                                    backgroundColor: '#e0e0e0',
                                    boxShadow: '0 -0.0625rem 0.125rem rgba(0, 0, 0, 0.05)',
                                    mb: 2
                                }} />

                                {/* Request a feature section */}
                                <Box sx={{ px: '1.25rem', pb: '0.625rem' }}>
                                    <Box
                                        onClick={handleRequestFeature}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            py: 1,
                                            px: 1.5,
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            mb: 1,
                                            '&:hover': {
                                                backgroundColor: '#e9ecef'
                                            }
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f4f4f' }}>
                                            Request a feature
                                        </Typography>
                                        <FeedbackOutlinedIcon sx={{ fontSize: '1rem', color: '#828282', ml: 1 }} />
                                    </Box>
                                </Box>

                                {/* User Profile Section in expanded view */}
                                <Box sx={{
                                    p: '1.25rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <img
                                            src={profilePicture}
                                            alt="User Avatar"
                                            style={{
                                                width: '2.5rem',
                                                height: '2.5rem',
                                                borderRadius: '50%',
                                                objectFit: 'cover'
                                            }}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = avatarPlaceholder;
                                            }}
                                        />
                                        <Box sx={{ marginLeft: '1rem', minWidth: 0, flex: 1 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: '100%'
                                                }}
                                            >
                                                {userName || userData?.fullName}
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: "0.7rem", fontWeight: 600 }}>
                                                USER
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <IconButton onClick={handleMenuClick}>
                                        <MoreVertIcon />
                                    </IconButton>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>

                {/* Menu */}
                <Menu
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            padding: '0.5rem',
                            borderRadius: '0.5rem',
                            boxShadow: '0 0.25rem 0.5rem rgba(0, 0, 0, 0.2)',
                        }
                    }}
                >
                    <MenuItem onClick={() => { handleMenuClose(); handleRequestFeature(); }}>
                        <FeedbackOutlinedIcon sx={{ fontSize: '1.25rem', color: '#757575', marginRight: '0.5rem' }} />
                        Request a Feature
                    </MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); logout(); }}>
                        <LogoutOutlinedIcon sx={{ fontSize: '1.25rem', color: '#757575', marginRight: '0.5rem' }} />
                        Logout
                    </MenuItem>
                </Menu>
            </Drawer>

            {/* Notifications */}
            <NotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={handleSnackbarClose}
            />

            <TeamRequestAFeatureModal
                open={isFeatureModalOpen}
                onClose={() => setIsFeatureModalOpen(false)}
                onSubmitSuccess={handleFeatureSubmitSuccess}
                onSubmitError={handleFeatureSubmitError}
            />
        </>
    );
};

export default TeamSidenav;
